import React, { useState, useEffect, useCallback, useRef } from 'react';
import { rondaApi } from '../services/api';
import './RondaModal.css';

const RondaModal = ({
  isOpen,
  onClose,
  selectedDate,
  selectedRonda,
  onRondaSelect
}) => {
  const [rondas, setRondas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(null);
  const scrollContainerRef = useRef(null);
  const selectedRondaRef = useRef(null);

  const fetchRondas = useCallback(async () => {
    if (!selectedDate) return;
    
    setLoading(true);
    setErrorMessage(null);
    try {
      const response = await rondaApi.getActiveRondas(selectedDate);
      const activeRondas = response.data.active_rondas || [];
      
      // Sort by ronda_codigo
      const sortedRondas = activeRondas.sort((a, b) => {
        return a.ronda_codigo.localeCompare(b.ronda_codigo);
      });
      
      setRondas(sortedRondas);
    } catch (error) {
      console.error('Error fetching rondas:', error);
      setRondas([]);
      const status = error.response?.status;
      if (status === 403) {
        setErrorMessage(error.response?.data?.message || 'Sem permissões para visualizar as rondas desta data.');
      } else if (status === 401) {
        setErrorMessage('Sessão expirada. Recarregue ou faça login novamente.');
      } else {
        setErrorMessage(error.response?.data?.message || 'Não foi possível carregar as rondas.');
      }
    } finally {
      setLoading(false);
    }
  }, [selectedDate]);

  // Fetch rondas when modal opens
  useEffect(() => {
    if (isOpen && selectedDate) {
      fetchRondas();
    }
  }, [isOpen, selectedDate, fetchRondas]);

  // Scroll to selected ronda when rondas are loaded
  useEffect(() => {
    if (rondas.length > 0 && selectedRonda && scrollContainerRef.current && selectedRondaRef.current) {
      // Small delay to ensure DOM is updated
      setTimeout(() => {
        const isMobile = window.innerWidth <= 768;
        selectedRondaRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: isMobile ? 'nearest' : 'center',
          inline: isMobile ? 'center' : 'nearest'
        });
      }, 100);
    } else if (rondas.length > 0 && scrollContainerRef.current) {
      // If no ronda selected, scroll to start
      const isMobile = window.innerWidth <= 768;
      if (isMobile) {
        scrollContainerRef.current.scrollLeft = 0;
      } else {
        scrollContainerRef.current.scrollTop = 0;
      }
    }
  }, [rondas, selectedRonda]);

  const handleRondaClick = (rondaCodigo) => {
    onRondaSelect(rondaCodigo);
    // Don't close modal - user can continue selecting
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="ronda-modal-backdrop" onClick={onClose} />

      {/* Modal Content */}
      <div className="ronda-modal" onClick={(e) => e.stopPropagation()}>
        <div className="ronda-modal-body">
          {loading ? (
            <div className="ronda-loading">Carregando rondas...</div>
          ) : errorMessage ? (
            <div className="ronda-error">
              <p>{errorMessage}</p>
            </div>
          ) : rondas.length > 0 ? (
            <div className="ronda-list" ref={scrollContainerRef}>
              {rondas.map((ronda) => (
                <div
                  key={ronda.ronda_codigo}
                  ref={selectedRonda === ronda.ronda_codigo ? selectedRondaRef : null}
                  className={`ronda-card ${selectedRonda === ronda.ronda_codigo ? 'selected' : ''}`}
                  onClick={() => handleRondaClick(ronda.ronda_codigo)}
                >
                  <div className="ronda-card-code">{ronda.ronda_codigo}</div>
                  <div className="ronda-card-name">{ronda.ronda_nome}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="no-rondas">Nenhuma ronda disponível para esta data</div>
          )}
        </div>
      </div>
    </>
  );
};

export default RondaModal;

